import { useEffect, useMemo, useState, type FormEvent } from "react";

import { ApiError } from "../../api/client";
import { useCreateOdometer } from "../../api/mutations";
import { useOdometerEntries } from "../../api/queries";
import type { CarSummary, FormOptionsResponse } from "../../api/types";
import Modal from "../../components/Modal";
import {
  formatMileage,
  odometerBounds,
  parseMileage,
  timestamp,
} from "../../lib/odometer";

interface OdometerEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  formOptions: FormOptionsResponse;
  activeCar: CarSummary | null;
}

export default function OdometerEntryModal({
  isOpen,
  onClose,
  formOptions,
  activeCar,
}: OdometerEntryModalProps) {
  const odometerQuery = useOdometerEntries(activeCar?.id ?? null);
  const createOdometer = useCreateOdometer();
  const [entryDate, setEntryDate] = useState(formOptions.now_local);
  const [mileage, setMileage] = useState("");
  const [manualMileage, setManualMileage] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<{ text: string; variant?: "error" } | null>(
    null,
  );

  const entries = odometerQuery.data?.entries ?? [];
  const bounds = useMemo(
    () => odometerBounds(entries, entryDate),
    [entries, entryDate],
  );

  useEffect(() => {
    if (!isOpen) return;
    setEntryDate(formOptions.now_local);
    setMileage(formatMileage(activeCar?.current_odometer_km));
    setManualMileage(false);
    setFieldErrors({});
    setStatus(null);
  }, [activeCar?.current_odometer_km, activeCar?.id, formOptions.now_local, isOpen]);

  useEffect(() => {
    if (!isOpen || manualMileage || !odometerQuery.data) return;
    setMileage(formatMileage(bounds.previous?.odometer_km));
  }, [bounds.previous?.odometer_km, isOpen, manualMileage, odometerQuery.data]);

  const mileageValue = parseMileage(mileage);
  const mileageError =
    mileage.trim() && mileageValue === null
      ? "Must be a valid number."
      : mileageValue !== null && mileageValue <= 0
        ? "Must be greater than 0."
        : mileageValue !== null &&
            bounds.previous !== null &&
            mileageValue < bounds.previous.odometer_km
          ? `Must be at least ${formatMileage(bounds.previous.odometer_km)} km.`
          : mileageValue !== null &&
              bounds.next !== null &&
              mileageValue > bounds.next.odometer_km
            ? `Must be at most ${formatMileage(bounds.next.odometer_km)} km.`
            : "";
  const dateError = entryDate.trim() && timestamp(entryDate) === null
    ? "Must be a valid date/time."
    : "";
  const hintParts = [
    bounds.previous ? `min ${formatMileage(bounds.previous.odometer_km)} km` : null,
    bounds.next ? `max ${formatMileage(bounds.next.odometer_km)} km` : null,
  ].filter(Boolean);
  const pending = createOdometer.isPending;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFieldErrors({});

    if (!activeCar) {
      setStatus({ text: "Select a car before adding mileage.", variant: "error" });
      return;
    }
    if (!entryDate.trim() || dateError) {
      setFieldErrors({ entry_date: dateError || "Required." });
      setStatus({ text: "Please fix the highlighted fields.", variant: "error" });
      return;
    }
    if (mileageValue === null || mileageError) {
      setFieldErrors({ odometer_km: mileageError || "Required." });
      setStatus({ text: "Please fix the highlighted fields.", variant: "error" });
      return;
    }

    setStatus({ text: "Saving..." });
    try {
      await createOdometer.mutateAsync({
        car_id: activeCar.id,
        odometer_km: mileageValue,
        entry_date: entryDate,
      });
      setStatus({ text: "Saved." });
      window.setTimeout(onClose, 250);
    } catch (err) {
      if (err instanceof ApiError && err.errors) {
        setFieldErrors(err.errors);
        setStatus({ text: "Please fix the highlighted fields.", variant: "error" });
      } else {
        const message = err instanceof Error ? err.message : String(err);
        setStatus({ text: `Could not save: ${message}`, variant: "error" });
      }
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add odometer entry">
      <form className="entry-form" onSubmit={handleSubmit} noValidate>
        <label className="field">
          <span className="field__label">Date</span>
          <input
            className="field__input"
            type="datetime-local"
            name="entry_date"
            required
            value={entryDate}
            onChange={(event) => {
              setEntryDate(event.target.value);
              setManualMileage(false);
              setFieldErrors((prev) => ({ ...prev, entry_date: "" }));
              setStatus(null);
            }}
          />
          <span className="field__error" data-error-for="entry_date">
            {fieldErrors.entry_date || dateError}
          </span>
        </label>

        <label className="field">
          <span className="field__label">Mileage</span>
          <input
            className="field__input"
            type="text"
            name="odometer_km"
            inputMode="decimal"
            autoComplete="off"
            required
            value={mileage}
            onChange={(event) => {
              setMileage(event.target.value);
              setManualMileage(true);
              setFieldErrors((prev) => ({ ...prev, odometer_km: "" }));
              setStatus(null);
            }}
          />
          {hintParts.length > 0 ? (
            <span className="field__help">{hintParts.join(" / ")}</span>
          ) : null}
          <span className="field__error" data-error-for="odometer_km">
            {fieldErrors.odometer_km || mileageError}
          </span>
        </label>

        {status ? (
          <div
            className="form-status"
            data-form-status
            data-variant={status.variant}
            role={status.variant === "error" ? "alert" : "status"}
          >
            {status.text}
          </div>
        ) : null}

        <div className="form-actions">
          <button
            className="button button--success button--equal"
            type="submit"
            disabled={pending || Boolean(mileageError) || Boolean(dateError)}
          >
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}
